// @workflow platform.viewer
/**
 * The app's wiring of the viewer adapter to today's implementation. With src/platform/viewer/
 * as a whole, this is the ONLY place allowed to import forgeService.js / forgeviewer.js
 * (tools/check-legacy-imports.mjs enforces it). When a capability's logic moves out of
 * forgeService, its dependency here changes and the adapter's callers do not.
 */
import Vue from 'vue';
import { takeScreenshot } from '@/scripts/js/forgeService';
import {
  attachmentExtension,
  isPdfViewer,
  isImageViewer,
  pdfViewerOpenPageByNumber,
  renderedLinkTagIds,
  highlightTagLinkElements,
  ensureDocLinkVisible,
  TurnViewerLockOff,
  launchPlanViewer,
} from '@/scripts/js/forgeviewer';
import { setPermeativeElementsTheme, getBBBox, zoomToFitBox2D } from '@/utils/forgeUtils';
import { getStreamingApi } from '@/utils/regionUtils';
import { error as debugError } from '@/utils/debugLogger';
import { $hexToVector4 } from '@/utils';
import { useSpatialFocus } from '@/features/Dashboard/composables/useSpatialFocus';
import dashboardWidgetsService from '@/features/Dashboard/service/dashboard-widgets.service';
import { buildSpatialRenderPlan } from '@/features/Dashboard/spatialFocus/spatialFocusArbitrator';
import { listLoadedModels, fitSpatialRenderPlan } from '@/features/Dashboard/spatialFocus/forgeSpatialFocusAdapter';
import { viewerPanes } from './panes.js';

/** forgeService's own toast for "no 3D viewer" (forgeService.js:2783). */
const WARNING_OPTIONS = Object.freeze({ position: 'top-right', timeout: 3000 });

const notify = Object.freeze({
  warning: (text) => Vue.prototype.$dialog.notify.warning(text, { ...WARNING_OPTIONS }),
});

/** Autodesk's event names, read when the viewer script has loaded (window.Autodesk). */
const events = Object.freeze({
  get OBJECT_TREE_CREATED() {
    return window.Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT;
  },
  get CAMERA_CHANGE() {
    return window.Autodesk.Viewing.CAMERA_CHANGE_EVENT;
  },
});

/** The document view's legacy functions (documents.js). */
const documentDeps = Object.freeze({
  isDrawingFile: (fileName) => {
    const ext = attachmentExtension(fileName);
    return isPdfViewer(ext) || isImageViewer(ext);
  },
  openPage: pdfViewerOpenPageByNumber,
  renderedTagIds: renderedLinkTagIds,
  highlightTags: highlightTagLinkElements,
  showLink: ensureDocLinkVisible,
});

/** The plan extensions the 360 mini map draws with (scans + marker). */
const PLAN_MINI_MAP_EXTENSIONS = Object.freeze(['labels3dExt', 'Plan2DExtension']);

/**
 * launchPlanViewer resolves before its extensions finish loading (planLoadCallback.js): resolves once
 * the plan viewer has both, on Forge's own "extension loaded" event. A load that never finishes leaves
 * the map on Radar, which is its loading state.
 */
function planExtensionsLoaded(store) {
  const viewer = store.state.planViewer;
  const hasAll = () => PLAN_MINI_MAP_EXTENSIONS.every((id) => viewer.getExtension(id));
  if (!viewer || hasAll()) return Promise.resolve();
  const event = window.Autodesk.Viewing.EXTENSION_LOADED_EVENT;
  return new Promise((resolve) => {
    const onLoaded = () => {
      if (!hasAll()) return;
      viewer.removeEventListener(event, onLoaded);
      resolve();
    };
    viewer.addEventListener(event, onLoaded);
  });
}

/** The 2D plan viewer's legacy functions (miniMap.js). */
const planDeps = (store) => Object.freeze({
  launch: async (urn, streamingApi, parentNode) => {
    await launchPlanViewer(urn, null, streamingApi, parentNode);
    await planExtensionsLoaded(store);
  },
  streamingApiFor: getStreamingApi,
  paneHost: () => document.getElementById('planViewer'),
  fitBox: (viewer, { min, max }) => {
    const { THREE } = window;
    zoomToFitBox2D(viewer, new THREE.Box3(new THREE.Vector3(min.x, min.y, 0), new THREE.Vector3(max.x, max.y, 0)));
  },
  reportError: (err) => debugError('[MiniMap] plan load failed:', err),
});

/**
 * @param {import('vuex').Store} store
 * @returns {object} the deps of core.js `createViewer`
 */
export function legacyViewerDeps(store) {
  return {
    store,
    panes: viewerPanes,
    // The spatial-focus state is shared per viewer by every caller, so one instance serves all.
    spatialFocus: useSpatialFocus(),
    resolveSpatialFocus: (body, options) => dashboardWidgetsService.resolveSpatialFocus(body, options),
    buildRenderPlan: buildSpatialRenderPlan,
    fitRenderPlan: fitSpatialRenderPlan,
    listLoadedModels,
    themeElements: setPermeativeElementsTheme,
    toThemingColor: $hexToVector4,
    boxOf: getBBBox,
    takeScreenshot,
    notify,
    events,
    unlockNavigation: TurnViewerLockOff,
    documents: documentDeps,
    plan: planDeps(store),
  };
}
